import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ChallengeResponse } from '@/contracts';
import { claimLandingTouch, createLandingTouch, fetchChallenge } from '@/api/teleopApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useClerkToken } from '@/hooks/useClerkToken';
import {
  buildChallengeAttributionState,
  buildChallengeAttributionStateFromLandingTouch,
  buildChallengeAuthReturnUrl,
  buildChallengeLandingTouchRequest,
  clearAttributionTokenFromUrl,
  getOrCreateAnonId,
  readAttributionTokenFromUrl,
  readChallengeMarketingFields,
  readStoredAttributionState,
  storeAttributionState,
  type ActiveAttributionState,
} from '@/lib/attribution';
import { trackAuthCompleted, trackChallengeCtaClicked, trackChallengeLandingViewed } from '@/lib/analytics';
import { buildLocalAuthPath, PLAY_CLAW_JWT_TEMPLATE } from '@/lib/auth';
import { logWarn } from '@/lib/logger';
import { enterMixpanelReplaySurface, leaveMixpanelReplaySurface } from '@/lib/mixpanel';

const DEFAULT_PAGE_TITLE = 'Play Claw by DwellBot Games';

type RemoteStatus = 'loading' | 'ready' | 'error';
type ClaimStatus = 'idle' | 'loading' | 'success' | 'error';

const readChallengeDescription = (challenge: ChallengeResponse): string => {
  const robotName = challenge.robot_name ?? 'our live claw machine';
  const description = `Watch a real ${robotName} challenge clip, then sign in to take your own turn on the live machine.`;

  return description;
};

const updateMetaTag = (selector: string, content: string): (() => void) => {
  const metaTag = document.querySelector<HTMLMetaElement>(selector);

  if (metaTag === null) {
    return () => undefined;
  }

  const previousContent = metaTag.getAttribute('content');
  metaTag.setAttribute('content', content);

  return () => {
    if (previousContent === null) {
      metaTag.removeAttribute('content');
      return;
    }

    metaTag.setAttribute('content', previousContent);
  };
};

const ChallengePreview = ({ challenge }: { challenge: ChallengeResponse }) => {
  if (challenge.preview_url) {
    return (
      <video
        className="h-full w-full object-cover"
        src={challenge.preview_url}
        poster={challenge.thumbnail_url ?? undefined}
        autoPlay
        controls
        loop
        muted
        playsInline
      />
    );
  }

  if (challenge.thumbnail_url) {
    return (
      <img
        className="h-full w-full object-cover"
        src={challenge.thumbnail_url}
        alt="Challenge preview"
      />
    );
  }

  return (
    <video
      aria-hidden="true"
      className="h-full w-full object-contain"
      src="/claw-animation.webm"
      autoPlay
      loop
      muted
      playsInline
    />
  );
};

const ChallengePage: React.FC = () => {
  const navigate = useNavigate();
  const { publicId } = useParams<{ publicId: string }>();
  const { getToken, isLoaded, isSignedIn } = useClerkToken(PLAY_CLAW_JWT_TEMPLATE);
  const [challenge, setChallenge] = useState<ChallengeResponse | null>(null);
  const [challengeStatus, setChallengeStatus] = useState<RemoteStatus>('loading');
  const [challengeError, setChallengeError] = useState<string | null>(null);
  const [attributionState, setAttributionState] = useState<ActiveAttributionState | null>(null);
  const [attributionStatus, setAttributionStatus] = useState<RemoteStatus>('loading');
  const [attributionError, setAttributionError] = useState<string | null>(null);
  const [claimStatus, setClaimStatus] = useState<ClaimStatus>('idle');
  const [claimError, setClaimError] = useState<string | null>(null);
  const attemptedClaimTokenRef = useRef<string | null>(null);

  useEffect(() => {
    enterMixpanelReplaySurface('challenge');

    return () => {
      leaveMixpanelReplaySurface('challenge');
    };
  }, []);

  useEffect(() => {
    attemptedClaimTokenRef.current = null;
    setChallenge(null);
    setChallengeStatus('loading');
    setChallengeError(null);
    setAttributionState(null);
    setAttributionStatus('loading');
    setAttributionError(null);
    setClaimStatus('idle');
    setClaimError(null);

    if (!publicId) {
      setChallengeStatus('error');
      setChallengeError('Challenge not found.');
      return;
    }

    let canceled = false;

    void (async () => {
      try {
        const nextChallenge = await fetchChallenge(publicId);

        if (canceled) {
          return;
        }

        setChallenge(nextChallenge);
        setChallengeStatus('ready');
      } catch (error) {
        if (canceled) {
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Challenge not found.';
        setChallengeStatus('error');
        setChallengeError(errorMessage);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [publicId]);

  useEffect(() => {
    if (challenge === null) {
      return;
    }

    const nextTitle = `${challenge.cta_headline} | ${DEFAULT_PAGE_TITLE}`;
    const nextDescription = readChallengeDescription(challenge);
    const previousTitle = document.title;
    const restoreDescription = updateMetaTag('meta[name="description"]', nextDescription);
    const restoreOgTitle = updateMetaTag('meta[property="og:title"]', nextTitle);
    const restoreOgDescription = updateMetaTag('meta[property="og:description"]', nextDescription);
    const restoreTwitterTitle = updateMetaTag('meta[name="twitter:title"]', nextTitle);
    const restoreTwitterDescription = updateMetaTag('meta[name="twitter:description"]', nextDescription);
    document.title = nextTitle;

    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreOgTitle();
      restoreOgDescription();
      restoreTwitterTitle();
      restoreTwitterDescription();
    };
  }, [challenge]);

  useEffect(() => {
    if (challenge === null) {
      return;
    }

    const urlToken = readAttributionTokenFromUrl();
    const anonId = getOrCreateAnonId();
    const storedState = readStoredAttributionState();

    if (urlToken) {
      const matchesStoredToken = storedState?.attributionToken === urlToken;
      const nextState = buildChallengeAttributionState({
        attributionToken: urlToken,
        anonId,
        landingTouchId: matchesStoredToken ? storedState.landingTouchId : null,
        challengePublicId: challenge.public_id,
        challengeShareId: matchesStoredToken ? storedState.challengeShareId : null,
      });

      storeAttributionState(nextState);
      clearAttributionTokenFromUrl();
      setAttributionState(nextState);
      setAttributionStatus('ready');
      setAttributionError(null);
      return;
    }

    let canceled = false;

    void (async () => {
      try {
        const request = buildChallengeLandingTouchRequest(challenge.public_id);
        const marketingFields = readChallengeMarketingFields();
        const response = await createLandingTouch(request);

        if (canceled) {
          return;
        }

        const nextState = buildChallengeAttributionStateFromLandingTouch(challenge.public_id, response);
        storeAttributionState(nextState);
        setAttributionState(nextState);
        setAttributionStatus('ready');
        setAttributionError(null);
        trackChallengeLandingViewed({
          challengePublicId: challenge.public_id,
          landingTouchId: response.landing_touch_id,
          challengeShareId: response.challenge_share_id ?? null,
          deduped: response.deduped,
          utmSource: marketingFields.utmSource,
          utmMedium: marketingFields.utmMedium,
          utmCampaign: marketingFields.utmCampaign,
          utmId: marketingFields.utmId,
        });
      } catch (error) {
        if (canceled) {
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Could not prepare challenge attribution.';
        setAttributionStatus('error');
        setAttributionError(errorMessage);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [challenge]);

  useEffect(() => {
    if (challenge === null) {
      return;
    }

    if (attributionState === null) {
      return;
    }

    if (!isLoaded || !isSignedIn) {
      return;
    }

    const attributionToken = attributionState.attributionToken;

    if (attemptedClaimTokenRef.current === attributionToken) {
      return;
    }

    attemptedClaimTokenRef.current = attributionToken;
    let canceled = false;
    setClaimStatus('loading');
    setClaimError(null);

    void (async () => {
      try {
        const authToken = await getToken();

        if (!authToken) {
          throw new Error('Missing auth token.');
        }

        const response = await claimLandingTouch(
          {
            attribution_token: attributionToken,
          },
          authToken
        );

        if (canceled) {
          return;
        }

        const nextState = buildChallengeAttributionState({
          attributionToken,
          anonId: attributionState.anonId,
          landingTouchId: response.landing_touch_id,
          challengePublicId: challenge.public_id,
          challengeShareId: response.challenge_share_id ?? attributionState.challengeShareId,
        });

        storeAttributionState(nextState);
        setAttributionState(nextState);
        setClaimStatus('success');
        trackAuthCompleted({
          anonId: attributionState.anonId,
          challengePublicId: challenge.public_id,
          landingTouchId: response.landing_touch_id,
          challengeShareId: response.challenge_share_id ?? null,
          claimStage: response.claim_stage,
          isFirstTouchForUser: response.is_first_touch_for_user,
          userId: response.user_id,
        });
      } catch (error) {
        if (canceled) {
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Could not finish sign-in attribution.';
        setClaimStatus('error');
        setClaimError(errorMessage);
        logWarn('challenge.claim.failed', 'Challenge attribution claim failed', {
          challenge_public_id: challenge.public_id,
          error: errorMessage,
        });
      }
    })();

    return () => {
      canceled = true;
    };
  }, [attributionState, challenge, getToken, isLoaded, isSignedIn]);

  if (challengeStatus === 'loading') {
    return (
      <div className="relative min-h-screen min-h-[100svh] w-full overflow-hidden bg-[#040712] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(248,196,113,0.16),transparent_40%),linear-gradient(180deg,rgba(8,13,28,0.94),rgba(3,6,16,1))]" />
        <div className="relative z-10 flex min-h-screen min-h-[100svh] items-center justify-center p-6 text-center">
          <div className="rounded-[2rem] border border-white/12 bg-black/35 px-6 py-5 text-white/80 backdrop-blur-md">
            Loading challenge...
          </div>
        </div>
      </div>
    );
  }

  if (challengeStatus === 'error' || challenge === null) {
    return (
      <div className="relative min-h-screen min-h-[100svh] w-full overflow-hidden bg-[#040712] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(248,196,113,0.16),transparent_40%),linear-gradient(180deg,rgba(8,13,28,0.94),rgba(3,6,16,1))]" />
        <div className="relative z-10 mx-auto flex min-h-screen min-h-[100svh] max-w-xl flex-col items-center justify-center gap-5 p-6 text-center">
          <Badge className="border-white/20 bg-white/10 text-white">Challenge</Badge>
          <h1 className="text-4xl font-semibold tracking-tight">Challenge unavailable</h1>
          <p className="text-white/75">{challengeError ?? 'This challenge link is not available.'}</p>
          <Button size="lg" onClick={() => navigate('/')}>
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  const challengeDescription = readChallengeDescription(challenge);
  const isAttributionPending = attributionStatus === 'loading';
  const isAuthPending = isLoaded === false;
  const isSignedInUser = isSignedIn === true;
  const isClaimPending = isSignedInUser && claimStatus === 'loading';
  const isPrimaryActionDisabled =
    challengeStatus !== 'ready' ||
    attributionState === null ||
    isAttributionPending ||
    isAuthPending ||
    isClaimPending;

  const handleNavigateToAuth = (path: '/sign-in' | '/sign-up', destination: 'sign_in' | 'sign_up') => {
    if (attributionState === null) {
      return;
    }

    const redirectUrl = buildChallengeAuthReturnUrl(attributionState.attributionToken);
    const authPath = buildLocalAuthPath(path, redirectUrl);
    trackChallengeCtaClicked({
      challengePublicId: challenge.public_id,
      landingTouchId: attributionState.landingTouchId,
      destination,
    });
    navigate(authPath);
  };

  const handlePlayNow = () => {
    trackChallengeCtaClicked({
      challengePublicId: challenge.public_id,
      landingTouchId: attributionState?.landingTouchId ?? null,
      destination: 'play',
    });
    navigate('/play');
  };

  return (
    <div className="relative min-h-screen min-h-[100svh] w-full overflow-hidden bg-[#040712] text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(248,196,113,0.18),transparent_38%),linear-gradient(180deg,rgba(8,13,28,0.94),rgba(3,6,16,1))]" />
      <div className="absolute left-1/2 top-[-7rem] h-[22rem] w-[22rem] -translate-x-1/2 rounded-full bg-[#f59e0b]/18 blur-3xl" />
      <div className="absolute bottom-[-8rem] left-[-5rem] h-[18rem] w-[18rem] rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="absolute right-[-4rem] top-1/3 h-[16rem] w-[16rem] rounded-full bg-fuchsia-500/10 blur-3xl" />
      <div className="relative z-10 mx-auto flex min-h-screen min-h-[100svh] w-full max-w-6xl flex-col justify-center gap-8 p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-3">
          <Badge className="border-white/20 bg-white/10 text-white">Challenge</Badge>
          {challenge.robot_name ? (
            <Badge className="border-white/12 bg-black/25 text-white/80">{challenge.robot_name}</Badge>
          ) : null}
          {challenge.game_type_key ? (
            <Badge className="border-white/12 bg-black/25 text-white/80">{challenge.game_type_key}</Badge>
          ) : null}
        </div>
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="overflow-hidden rounded-[2rem] border border-white/12 bg-black/30 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-md">
            <div className="aspect-[9/16] bg-black/40">
              <ChallengePreview challenge={challenge} />
            </div>
          </div>
          <div className="flex flex-col justify-between gap-6 rounded-[2rem] border border-white/12 bg-black/35 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-md">
            <div className="space-y-5">
              <div className="space-y-3">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#f8c471]">
                  Someone shared their run
                </p>
                <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
                  {challenge.cta_headline}
                </h1>
                <p className="max-w-xl text-base leading-7 text-white/78 sm:text-lg">
                  {challengeDescription}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-4">
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/60">How it works</p>
                  <p className="mt-2 text-sm leading-6 text-white/80">
                    Join the live queue, take control of the real claw machine, and try to beat the clip you just
                    watched.
                  </p>
                </div>
                <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-4">
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/60">What you need</p>
                  <p className="mt-2 text-sm leading-6 text-white/80">
                    A quick sign-in is required before you can join the queue and start a live session.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {isSignedInUser ? (
                <Button size="lg" className="w-full" disabled={isPrimaryActionDisabled} onClick={handlePlayNow}>
                  {challenge.cta_label}
                </Button>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Button
                    size="lg"
                    className="w-full"
                    disabled={isPrimaryActionDisabled}
                    onClick={() => handleNavigateToAuth('/sign-in', 'sign_in')}
                  >
                    Sign in to play
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-white/20 bg-white/5 text-white hover:bg-white/10"
                    disabled={isPrimaryActionDisabled}
                    onClick={() => handleNavigateToAuth('/sign-up', 'sign_up')}
                  >
                    Create account
                  </Button>
                </div>
              )}
              <div className="min-h-12 rounded-[1.25rem] border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/72">
                {isAttributionPending ? 'Preparing your challenge link...' : null}
                {!isAttributionPending && attributionError ? attributionError : null}
                {!isAttributionPending && !attributionError && isAuthPending ? 'Checking your account...' : null}
                {!isAttributionPending && !attributionError && !isAuthPending && isClaimPending
                  ? 'Finishing sign-in...'
                  : null}
                {!isAttributionPending && !attributionError && claimError ? claimError : null}
                {!isAttributionPending && !attributionError && !isAuthPending && !isClaimPending && !claimError
                  ? 'Live queue access starts right after sign-in.'
                  : null}
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-white/65">
                <Link
                  className="underline decoration-white/25 underline-offset-4 transition hover:text-white hover:decoration-white/60"
                  to="/"
                >
                  Home
                </Link>
                <Link
                  className="underline decoration-white/25 underline-offset-4 transition hover:text-white hover:decoration-white/60"
                  to="/privacy-policy"
                >
                  Privacy Policy
                </Link>
                <Link
                  className="underline decoration-white/25 underline-offset-4 transition hover:text-white hover:decoration-white/60"
                  to="/terms-of-service"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengePage;
